import { List, Map } from "immutable";
import jsonAbc from "jsonabc";
import { generateOptions } from "translations/options";
import { locales } from "./index";
import {
  ComputedFormModel,
  EmailSelection,
  EmailTranslation,
  FormValue,
  LocalesType,
  Search,
  SearchType,
  Selection,
  Suggestion,
  TranslationSimpleType,
  TranslationType,
  TranslationTypes,
} from "./types";

export const TAG_DE_TODO = 2;

export const TAG_FR_IGNORE = 11;
export const TAG_DE_IGNORE = 12;

export const languageTagMap: {
  [key: string]: { ignore: number; todo: number };
} = {
  de: { ignore: TAG_DE_IGNORE, todo: TAG_DE_TODO },
};

export const searchTags: {
  [key: number]: {
    apply?: (values: LocalesType) => LocalesType;
    label: string;
    lang: string;
  };
} = {
  [TAG_DE_IGNORE]: {
    apply: (values: LocalesType) => ({ ...values, de: "" }),
    label: "IGNORE",
    lang: "DE",
  },
  [TAG_DE_TODO]: { label: "TODO", lang: "DE" },
};

export function applyTagsLogic(translation: TranslationType): TranslationType {
  if (!translation.tags || !(translation.tags.length > 0)) return translation;

  return translation.tags.reduce((red: TranslationType, tag: number) => {
    const applyTag = searchTags[tag]?.apply;

    if (!applyTag || typeof applyTag !== "function") return red;

    return {
      ...red,
      locales: applyTag(red.locales),
    };
  }, translation);
}

export function containsTag(
  value: TranslationType,
  searchTags: Array<number>,
): boolean {
  if (!(searchTags.length > 0)) return true;
  const existingTags = searchTags.map((tag) => !!value?.tags?.includes(tag));
  return !existingTags.includes(false);
}

export function isContentSearchedFor(search: Search, value: any) {
  const { de, en } = value.locales;
  const searchText = search.text ? search.text.trim().toLowerCase() : "";

  const withTag = containsTag(value, search.tags);
  const withText =
    searchText && typeof en === "string" && typeof de === "string"
      ? en.toLowerCase().includes(searchText) ||
        de.toLowerCase().includes(searchText)
      : true;

  return withTag && withText;
}

export const isLocaleValue = (data: Record<string, any>) =>
  data?.id &&
  data.locales != null &&
  Object.keys(locales).some((key) => data.locales[key] != null);

export function arrayToMap<T extends EmailTranslation | TranslationType>(
  data: Array<T>,
): Map<string, any> {
  return data.reduce<Map<string, any>>(
    (map, { id, ...rest }) => map.mergeDeepIn(id.split("."), { ...rest, id }),
    Map(),
  );
}

const filterNestedContent = (
  data: Record<string, any>,
  search: Search,
): boolean =>
  Object.entries(data).reduce(
    (acc: boolean, cur: [string, TranslationType]) => {
      if (acc) {
        const [, value] = cur;

        if (isLocaleValue(value)) return !isContentSearchedFor(search, value);
        else if (value != null && typeof value === "object")
          return filterNestedContent(value, search);

        return acc;
      }
      return acc;
    },
    true,
  );

function getContextSearch(
  search: string | null | undefined,
  length: number,
): string {
  if (!search) return "";

  const trimmed =
    search.charAt(search.length - 1) === "." ? search.slice(0, -1) : search;

  const fit = trimmed.length > length ? trimmed.slice(0, length) : trimmed;
  return fit || "";
}

export const filterNestedContext = (
  data: Record<string, any>,
  search: Search,
): boolean =>
  Object.entries(data).reduce((acc, cur) => {
    if (acc) {
      const [key, value] = cur;
      const fitSearch = getContextSearch(search.text, key.length);

      if (key.includes(fitSearch)) return !containsTag(value, search.tags);
      else if (value != null && typeof value === "object")
        return filterNestedContext(value, search);
      return acc;
    }
    return acc;
  }, true as boolean);

export const isFilteredBySearch = (
  search: Search,
  { key, value }: { key: string; value: any },
) => {
  if (!search || (search.text.length === 0 && search.tags.length === 0))
    return false;

  switch (search.type) {
    case SearchType.Context: {
      if (isLocaleValue(value))
        return !(
          containsTag(value, search.tags) &&
          (search.text ? key.includes(search.text) : true)
        );
      const fitSearch = getContextSearch(search.text, key.length);
      return key.includes(fitSearch)
        ? !containsTag(value, search.tags)
        : filterNestedContext(value, search);
    }
    case SearchType.Content: {
      if (isLocaleValue(value)) return !isContentSearchedFor(search, value);
      return filterNestedContent(value, search);
    }
    default:
      return false;
  }
};

export const getEmptySelection = (): Selection => [
  "",
  {
    description: "",
    id: "-1",
    links: [],
    locales: Object.keys(locales).reduce(
      (values, key) => ({
        ...values,
        [key]: "",
      }),
      {} as LocalesType,
    ),
    tags: [],
  },
];

export function isSelection<S extends EmailSelection | Selection>(
  selection: S,
  key: string,
) {
  const [selectedKey] = selection;
  return selectedKey === key;
}

export const isKeyValid = (value: FormValue) => {
  if (typeof value !== "string") return "Something is terribly wrong";

  const trimmed = value.trim();

  if (!trimmed) return "Name is required";

  // allow only number strings
  if (/^\d+$/.test(trimmed)) return null;

  if (!/^([a-zA-Z])*$/g.test(trimmed))
    return "Name should only contain letters or only numbers, not mixed";

  if (!/^[a-z][a-zA-Z]*$/.test(trimmed))
    return "Name should always start with a lowercase letter";

  return null;
};

type MacroValidator = { [macro: string]: number };

function getTemplates(translation: string): MacroValidator {
  const match = translation.match(/\{\{([^\s{}])*\}\}/gi);
  if (match != null && match.length > 0)
    return match.reduce((obj: MacroValidator, group) => {
      if (obj[group] != null) return { ...obj, [group]: obj[group] + 1 };
      return { ...obj, [group]: 1 };
    }, {} as MacroValidator);
  return {};
}

function getMacros(
  translation: string | null | undefined,
): MacroValidator | null {
  if (!translation) return null;

  const options = generateOptions();

  const base = options.reduce((obj: any, { syntax }: any) => {
    if (!syntax) return obj;

    const regex = new RegExp(syntax, "g");
    const match = translation.match(regex);
    if (match != null && match.length > 0) {
      return { ...obj, [syntax]: match.length };
    }
    return obj;
  }, {});

  const macros = { ...base, ...getTemplates(translation) };

  return macros;
}

function validateMacros(
  translation: string | null | undefined,
  macros: MacroValidator | null,
): boolean {
  if (!macros) return true;

  return Object.entries(macros).every(([macro, count]) => {
    const regexp = new RegExp(macro, "g");
    const match = translation?.match(regexp);
    return match && match.length === count;
  });
}

export function validateTemplate(
  translation: string | null | undefined,
): string | null {
  if (!translation) return null;

  const templates = translation
    .match(/\{\{([^\s{}])*\}\}/gi)
    ?.map((template) => template.substring(2, template.length - 2));

  if (!templates) return null;

  const errorTemplate = templates.find(
    (template) => template.match(/^[a-zA-Z\-_]*$/gm) == null,
  );

  if (!errorTemplate) return null;

  return `"${errorTemplate}" is not a valid template name, it should not contain spaces or special characters`;
}

export const isTranslationValid =
  (locale: string) =>
  (value: FormValue | null | undefined, formState: ComputedFormModel) => {
    if (
      !value ||
      (typeof value === "string" && value.length === 0) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      if (
        !languageTagMap[locale] ||
        !(
          Array.isArray(formState.tags.value) &&
          formState.tags.value.find(
            (tag) =>
              tag === languageTagMap[locale].ignore ||
              tag === languageTagMap[locale].todo,
          )
        )
      )
        return "Translation value cannot be empty !";
    }

    if (typeof value === "string") {
      const missingBrackets = value.split("").reduce((acc, cur) => {
        if (cur === "}") return acc - 1;
        if (cur === "{") return acc + 1;
        return acc;
      }, 0);
      if (missingBrackets > 0) return "Detected a bracket that was not closed";

      const macros = getMacros(formState?.en?.value as string);

      if (
        macros &&
        Object.values(macros).some((macroCount) => macroCount > 1)
      ) {
        return "Cannot have mutlple macros of the same name";
      }

      const validMacros = validateMacros(value, macros);
      if (!validMacros) return "Macros do not match between translations";

      const templateError = validateTemplate(value);
      if (templateError != null) return templateError;
    }

    return null;
  };

export const arrayToState = (arr: Readonly<TranslationTypes>) =>
  arrayToMap(
    Array.from(arr).map((t) => ({
      ...t,
      description: t.description || "",
      links: t.links || [],
      locales: Object.keys(locales).reduce(
        (r, locale) => ({
          ...r,
          [locale]: t.locales?.[locale as keyof LocalesType] || "",
        }),
        {} as LocalesType,
      ),
      tags: t.tags || [],
    })),
  );

export function getOntology(
  data: TranslationType | undefined,
  key: keyof TranslationType["locales"],
): string {
  if (!data?.locales) return "";

  return data.locales[key];
}

const getKey = (key: string, prevKey: string | null | undefined) =>
  prevKey ? `${prevKey}.${key}` : key;

function recursiveMapFlatten(
  data: Record<string, any>,
  prevKey: string | null | undefined,
): List<any> {
  return Object.entries(data).reduce(
    (acc: List<any>, [k, v]: [string, Record<string, any>]): List<any> => {
      if (getKey(k, prevKey) === v.id) return acc.push(v);
      return acc.concat(recursiveMapFlatten(v, getKey(k, prevKey))).toList();
    },
    List(),
  );
}

export function convertToDbArray(data: Map<string, any>): Array<any> {
  return recursiveMapFlatten(data.toJS(), null).toArray();
}

// Returns IDs that would cause immutable's setIn to throw "invalid keyPath":
// - IDs with empty segments (e.g. "foo..bar", ".foo")
// - IDs whose dot-path prefix is already a leaf (e.g. "foo.bar" and "foo.bar.baz" coexist)
type ConflictEntry = Pick<TranslationType, "id" | "updated_at" | "created_at">;

export function findConflictingIds(
  translationsArray: Readonly<Array<TranslationType>>,
): {
  emptySegments: ConflictEntry[];
  prefixConflicts: Array<{ child: ConflictEntry; parent: ConflictEntry }>;
} {
  const entries = translationsArray.filter(
    (t): t is TranslationType & { id: string } => Boolean(t.id),
  );

  const byId: Record<string, TranslationType & { id: string }> =
    Object.fromEntries(entries.map((t) => [t.id, t]));
  const emptySegments: ConflictEntry[] = [];
  const prefixConflicts: Array<{
    child: ConflictEntry;
    parent: ConflictEntry;
  }> = [];

  const toEntry = (t: TranslationType): ConflictEntry => ({
    created_at: t.created_at,
    id: t.id,
    updated_at: t.updated_at,
  });

  for (const t of entries) {
    const parts = t.id.split(".");
    if (parts.some((p) => p === "")) {
      emptySegments.push(toEntry(t));
      continue;
    }
    for (let i = 1; i < parts.length; i++) {
      const prefix = parts.slice(0, i).join(".");
      const parentEntry = byId[prefix];
      if (parentEntry) {
        prefixConflicts.push({
          child: toEntry(t),
          parent: toEntry(parentEntry),
        });
        break;
      }
    }
  }

  return { emptySegments, prefixConflicts };
}

export function convertDbToLocaleJson(
  locale: "de" | "en",
  translationsArray: Readonly<Array<TranslationType>>,
): TranslationSimpleType {
  const { emptySegments, prefixConflicts } =
    findConflictingIds(translationsArray);
  if (emptySegments.length > 0)
    console.error(
      "[convertDbToLocaleJson] IDs with empty segments:",
      emptySegments,
    );
  if (prefixConflicts.length > 0)
    console.error(
      "[convertDbToLocaleJson] prefix conflicts (child blocked by parent leaf):",
      prefixConflicts,
    );

  const json = translationsArray.reduce<Map<string, any>>(
    (map, { id, locales: translations }) => {
      if (!id) {
        console.error("[convertDbToLocaleJson] skipping entry with missing id");
        return map;
      }
      return map.setIn(id.split("."), translations[locale] || "");
    },
    Map(),
  );

  return JSON.parse(jsonAbc.sort(JSON.stringify(json.toJS())));
}

export function convertDbToIdJson(
  translationsArray: Readonly<Array<TranslationType>>,
): TranslationSimpleType {
  const json = translationsArray.reduce<Map<string, any>>((map, { id }) => {
    if (!id) {
      console.error("[convertDbToIdJson] skipping entry with missing id");
      return map;
    }
    return map.setIn(id.split("."), id);
  }, Map());

  return JSON.parse(jsonAbc.sort(JSON.stringify(json.toJS())));
}

export const translationSuggestions: Suggestion[] = [
  {
    complete: (args: Array<string>) => `{select:${args[0]}}`,
    description: "select part of the translation to apply style to it",
    name: "Select",
    requirements: [{ name: "Selected text" }],
    trigger: "{select:",
  },
  {
    complete: (args: Array<string>) => `{{${args[0]}}}`,
    description: "insert values dynamically in the translations",
    name: "Template",
    requirements: [
      {
        dictionary: [
          "insuranceName",
          "providerName",
          "clinicName",
          "accountName",
          "patientId",
          "prevStatus",
          "newStatus",
          "status",
          "email",
          "count",
          "total",
          "date",
          "name",
          "reason",
          "message",
        ],
        name: "Value name",
      },
    ],
    trigger: "{{",
  },
];

export function regexpIndexOf(str: string, regexp: RegExp, inStart?: number) {
  const start = inStart != null ? inStart : 0;
  const indexInSuffix = str.slice(start).search(regexp);
  return indexInSuffix < 0 ? indexInSuffix : indexInSuffix + start;
}

export function regexpLastIndexOf(str: string, regexp: RegExp) {
  let ret = -1;
  let flag = 0;
  for (
    let i = regexpIndexOf(str, regexp, 0);
    i >= 0 && i <= str.length;
    i = regexpIndexOf(str, regexp, i)
  ) {
    if (i != -1) ret = i;
    if (flag === i) break;
    flag = i;
    i++;
  }
  return ret;
}

export const SEPARATOR_REGEXP = /(\s|\n)/gi;

export const indexOfSeparator = (str: string, start?: number) =>
  regexpIndexOf(str, SEPARATOR_REGEXP, start);

export const lastIndexOfSeparator = (str: string) =>
  regexpLastIndexOf(str, SEPARATOR_REGEXP);

export const isDev = window?.location?.hostname === "localhost";

function toDate(value?: number) {
  if (!value) return "";
  return new Date(value * 1000).toLocaleDateString("en-EN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function convertTranslationsToCsv(
  translations: Readonly<TranslationType[]>,
) {
  const csvArray = [
    `id;created_at;updated_at;en;de;description\r\n`,
    ...translations.map(
      ({ created_at, description, id, locales, updated_at }) => {
        return `${id ?? ""};${toDate(created_at)};${toDate(updated_at)};${
          locales?.en ?? ""
        };${locales?.de ?? ""};${description ?? ""}\r\n`;
      },
    ),
  ];

  const blob = new Blob(csvArray, { type: "text/csv;charset=utf-8;" });

  return window.URL.createObjectURL(blob);
}
